/**
 * Centralized Logging Service
 *
 * Provides structured logging with different log levels.
 * In development: logs to console with emojis for easy identification
 * In production: can be extended to send logs to external services (Sentry, etc.)
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
  context?: string;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private isProduction = import.meta.env.PROD;

  /**
   * Internal logging method
   */
  private log(level: LogLevel, message: string, data?: any, context?: string) {
    const entry: LogEntry = {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
      context,
    };

    // Development: Enhanced console logging with emojis
    if (this.isDevelopment) {
      const emoji = this.getEmoji(level);
      const prefix = context ? `[${context}]` : '';
      const logMessage = `${emoji} ${prefix} ${message}`;

      switch (level) {
        case 'error':
          console.error(logMessage, data || '');
          break;
        case 'warn':
          console.warn(logMessage, data || '');
          break;
        case 'debug':
          // Only log debug in development
          console.debug(logMessage, data || '');
          break;
        default:
          console.log(logMessage, data || '');
      }
    }

    // Production: Send errors to external service (Sentry, etc.)
    if (this.isProduction && level === 'error') {
      this.sendToErrorService(entry);
    }
  }

  /**
   * Get emoji for log level
   */
  private getEmoji(level: LogLevel): string {
    switch (level) {
      case 'error':
        return '❌';
      case 'warn':
        return '⚠️';
      case 'debug':
        return '🔍';
      default:
        return 'ℹ️';
    }
  }

  /**
   * Send error to external error reporting service
   * TODO: Integrate with Sentry when ready
   */
  private sendToErrorService(_entry: LogEntry) {
    // TODO: Uncomment when Sentry is configured
    // if (window.Sentry) {
    //   if (entry.data instanceof Error) {
    //     window.Sentry.captureException(entry.data, {
    //       tags: { context: entry.context },
    //       extra: { message: entry.message }
    //     });
    //   } else {
    //     window.Sentry.captureMessage(entry.message, {
    //       level: 'error',
    //       tags: { context: entry.context },
    //       extra: entry.data
    //     });
    //   }
    // }
  }

  /**
   * Log informational message
   */
  info(message: string, data?: any, context?: string) {
    this.log('info', message, data, context);
  }

  /**
   * Log warning message
   */
  warn(message: string, data?: any, context?: string) {
    this.log('warn', message, data, context);
  }

  /**
   * Log error message
   */
  error(message: string, error?: any, context?: string) {
    this.log('error', message, error, context);
  }

  /**
   * Log debug message (only in development)
   */
  debug(message: string, data?: any, context?: string) {
    if (this.isDevelopment) {
      this.log('debug', message, data, context);
    }
  }

  /**
   * Log performance metric
   */
  performance(operation: string, duration: number, context?: string) {
    const message = `${operation} completed in ${duration}ms`;
    if (duration > 1000) {
      this.warn(message, { duration }, context);
    } else {
      this.debug(message, { duration }, context);
    }
  }

  /**
   * Log API call
   */
  apiCall(method: string, endpoint: string, status?: number, duration?: number) {
    const message = `${method} ${endpoint}${status ? ` [${status}]` : ''}${duration ? ` (${duration}ms)` : ''}`;
    if (status && status >= 400) {
      this.error(message, { method, endpoint, status, duration }, 'API');
    } else if (status && status >= 300) {
      this.warn(message, { method, endpoint, status, duration }, 'API');
    } else {
      this.info(message, { method, endpoint, status, duration }, 'API');
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for use in other files
export type { LogLevel };
